<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import Konva from "konva";
import { Popup, showLoadingToast } from "vant";
import i18n from "@/locales";
import { AnimationGalleryInstance } from "@/components/mobile/media/AnimationGallery.vue";
import {
  durationGetter,
  usePerformAnimationWithCanvas,
} from "@/components/mobile/generation/animation/usePerformAnimation";
import { MateTraitAnimation } from "@/types/entity";
import Stage = Konva.Stage;
import Layer = Konva.Layer;
import Image = Konva.Image;

const FRAME_IMAGE_NAME = "animationImage";

export interface AnimationImageShape extends Konva.Image {
  attrs: {
    mateTraitAnimationId: number;
  };
}

interface AddImageToStageProps {
  stage: Stage;
  layer: Layer;
  draggable?: boolean;
  imageShapes: Image[];
  maxCount?: number;
}

const props = withDefaults(defineProps<AddImageToStageProps>(), {
  draggable: true,
  maxCount: 1,
});
const emit = defineEmits<{
  (e: "update:imageShapes", params: Image[]): void;
}>();
// State
const stage = computed(() => props.stage);
const layer = computed(() => props.layer);

const imgList = ref<MediaUploadFileItem[]>([]);
const showMediaSelection = ref(false);
const animationGalleryRef = ref<AnimationGalleryInstance | null>(null);

// Lifecycle
watch(
  () => showMediaSelection.value,
  (show) => {
    if (!show) {
      animationGalleryRef.value?.clear();
    }
  }
);

// Event Handler
const onSelected = async (animations: MateTraitAnimation[]) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  try {
    for (const item of animations) {
      await addAnimation(item, {
        mateTraitAnimationId: item.id,
      });
    }
    imgList.value = [];
    showMediaSelection.value = false;
  } finally {
    loading.close();
  }
};

const onClick = () => {
  if (props.imageShapes.length >= props.maxCount!) {
    return;
  }
  showMediaSelection.value = true;
};

const addAnimation = async (
  animation: MateTraitAnimation,
  params: {
    transform?: MediaTransform;
    mateTraitAnimationId?: number;
    id?: string;
  } = {}
) => {
  const urls = animation.portraitzz.map(
    (portrait) => portrait.portrait!.media!.url
  );
  const { play, animationShape } = usePerformAnimationWithCanvas(
    stage,
    layer,
    ref(urls),
    {
      draggable: true,
      durationGetter: (index) => durationGetter(index, animation.type),
      nodeProps: { ...params, name: FRAME_IMAGE_NAME },
    }
  );
  await play();
  emit("update:imageShapes", [...props.imageShapes, animationShape.value!]);
};

export interface AddAnimationToStageInstance {
  addAnimation: typeof addAnimation;
}

defineExpose({
  addAnimation,
});
</script>
<template>
  <div @click="onClick">
    <slot>
      <button class="btn btn-neutral btn-sm">
        {{ $t("Animation.insert") }}
      </button>
    </slot>
  </div>
  <popup
    v-model:show="showMediaSelection"
    class="h-[80%]"
    position="bottom"
    round
  >
    <animation-gallery
      ref="animationGalleryRef"
      @back="showMediaSelection = false"
      @on-confirm="onSelected"
    />
  </popup>
</template>
